import { MatrixTypeDto, TeamDevelopmentFilterDto, TeamPlanStatusDto } from '@/api/generated';
import { TeamMdrField } from '@/views/team/team.config.ts';

export type TeamTranslation = {
  actions: {
    approve: string;
    collapse: string;
    openProfile: string;
    preview: string;
    reject: string;
    retry: string;
    showMore: string;
    showRemaining: string;
    startFocusMode: string;
  };
  birthday: {
    tooltip: string;
  };
  filters: {
    all: string;
    label: string;
    options: Record<TeamDevelopmentFilterDto, string>;
    overdueSteps: string;
    plansToApprove: string;
    withoutPlan: string;
  };
  focus: {
    acceptanceStatus: string;
    clickToProfile: string;
    closePreview: string;
    emptyFilter: string;
    fullProfile: string;
    milestones: string;
    noActionNeeded: string;
    noActivePlan: string;
    pathLoadError: string;
    planDevelopment: string;
    planSteps: string;
    progress: string;
  };
  list: {
    columns: {
      employee: string;
      matrixField: string;
      planProgress: string;
      pmc: string;
      sop: string;
      status: string;
    };
    count: string;
    empty: string;
    loadError: string;
    loadingMore: string;
    loadMore: string;
    mdrAll: string;
    mdrLabel: string;
    searchPlaceholder: string;
    showingCount: string;
    statusAll: string;
    statusLabel: string;
  };
  matrix: {
    definition: string;
    developmentDirection: string;
    empty: string;
    loadError: string;
    loadMoreError: string;
    plan: string;
    progressTooltip: string;
    tenureYears: string;
    tenureYearsValue: string;
    tenureYearsValue_other: string;
  };
  mdr: Record<TeamMdrField, TeamMdrTranslation> & {
    fallback: string;
  };
  overview: {
    empty: string;
    loadError: string;
  };
  status: Record<TeamPlanStatusDto, string>;
  tabs: {
    list: string;
    overview: string;
  };
  toast: {
    acceptanceError: string;
    acceptanceSuccess: string;
  };
};

type TeamMdrTranslation = {
  description: string;
  label: string;
};

export const teamTranslation: TeamTranslation = {
  actions: {
    approve: 'Approve',
    collapse: 'Collapse',
    openProfile: 'Open profile',
    preview: 'Preview',
    reject: 'Reject',
    retry: 'Try again',
    showMore: 'Show more',
    showRemaining: 'Show remaining',
    startFocusMode: 'Start Focus Mode',
  },
  birthday: {
    tooltip: 'Birthday today',
  },
  filters: {
    all: 'Whole team',
    label: 'Team filters and statistics',
    options: {
      [TeamDevelopmentFilterDto.All]: 'Whole team',
      [TeamDevelopmentFilterDto.OverdueSteps]: 'Overdue steps',
      [TeamDevelopmentFilterDto.PlansToApprove]: 'Plans to approve',
      [TeamDevelopmentFilterDto.WithoutPlan]: 'Without development plan',
    },
    overdueSteps: 'Overdue steps',
    plansToApprove: 'Plans to approve',
    withoutPlan: 'Without development plan',
  },
  focus: {
    acceptanceStatus: 'Acceptance status',
    clickToProfile: 'Click to open profile',
    closePreview: 'Close preview',
    emptyFilter: 'No employees in this filter',
    fullProfile: 'Full employee profile',
    milestones: 'Milestones',
    noActionNeeded: 'This employee requires no action from you at this time.',
    noActivePlan: 'No active plan to preview.',
    pathLoadError: 'Could not load plan details.',
    planDevelopment: 'Development plan',
    planSteps: '{{count}} steps',
    progress: 'Plan progress',
  },
  list: {
    columns: {
      employee: 'Employee',
      matrixField: 'Matrix field',
      planProgress: 'Plan progress',
      pmc: 'PMC',
      sop: 'SOP',
      status: 'Plan status',
    },
    count: '{{count}} employees',
    empty: 'No results',
    loadError: 'Could not load employee list.',
    loadingMore: 'Loading...',
    loadMore: 'Load more employees',
    mdrAll: 'All MDR categories',
    mdrLabel: 'MDR category',
    searchPlaceholder: 'Search by last name, position...',
    showingCount: 'Showing {{shown}} of {{total}} employees',
    statusAll: 'All statuses',
    statusLabel: 'Plan status',
  },
  matrix: {
    definition: 'Definition',
    developmentDirection: 'Development direction',
    empty: 'No employees',
    loadError: 'Could not load matrix.',
    loadMoreError: 'Could not load more people.',
    plan: 'Plan {{progress}}%',
    progressTooltip: 'Plan progress: {{progress}}%',
    tenureYears: 'Tenure at JMP',
    tenureYearsValue: '{{count}} yr',
    tenureYearsValue_other: '{{count}} yrs',
  },
  mdr: {
    fallback: 'Outside MDR',
    [MatrixTypeDto.Alert]: {
      description:
        'Employees whose current results are unsatisfactory or have deteriorated and require a clearly defined corrective plan.',
      label: 'Alert',
    },
    [MatrixTypeDto.EffectiveInRole]: {
      description:
        'Employees who reliably deliver goals and currently develop as experts in their area.',
      label: 'Effective in Role',
    },
    [MatrixTypeDto.EmergingLeader]: {
      description:
        'Employees with high leadership potential and expected results, ready for one-level growth.',
      label: 'Emerging Leader',
    },
    [MatrixTypeDto.FutureLeader]: {
      description:
        'Employees with high leadership potential and high results, ready for growth by two or more levels.',
      label: 'Future Leader',
    },
    [MatrixTypeDto.NewEmployee]: {
      description:
        'New employees in onboarding whose potential and development direction are still being observed.',
      label: 'New Employee',
    },
    [MatrixTypeDto.QuestionMark]: {
      description:
        'Employees who need support and time to confirm role fit or discover their capabilities.',
      label: 'Question Mark',
    },
    [MatrixTypeDto.SubjectMatterLeader]: {
      description:
        'Employees with leadership potential and regular results, ready to expand responsibility or move horizontally.',
      label: 'Subject Matter Leader',
    },
    OUTSIDE_MDR: {
      description: 'Employees with an individual development plan outside the company MDR process.',
      label: 'Outside MDR',
    },
  },
  overview: {
    empty: 'No employees match the criteria',
    loadError: 'Could not load employees.',
  },
  status: {
    [TeamPlanStatusDto.Approved]: 'Approved',
    [TeamPlanStatusDto.Draft]: 'Draft',
    [TeamPlanStatusDto.None]: 'No plan',
    [TeamPlanStatusDto.Overdue]: 'Plan overdue',
    [TeamPlanStatusDto.Waiting]: 'Waiting for approval',
  },
  tabs: {
    list: 'Employee list',
    overview: 'Team overview',
  },
  toast: {
    acceptanceError: 'Could not update plan status.',
    acceptanceSuccess: 'Plan status updated.',
  },
};
